import { useState, useMemo, useCallback } from 'react';
import { adminEvents, eventTypeLabels, eventTypeIcons, eventTypeColors, eventStatusLabels, eventModalityLabels, eventModalityIcons, eventCategoryLabels, eventCategoryIcons, type RadioEvent, type EventCategory } from '@/mocks/adminEvents';
import { radioStations } from '@/mocks/radios';
import { useReminders } from '@/hooks/useReminders';
import { SwalAlert } from '@/hooks/useSweetAlert';

const INTEREST_KEY = 'rfw_app_events_interested';
const ATTEND_KEY = 'rfw_app_events_attending';

function loadSet(key: string): Set<number> {
  try { const raw = localStorage.getItem(key); return raw ? new Set(JSON.parse(raw)) : new Set(); } catch { return new Set(); }
}
function saveSet(key: string, set: Set<number>) { localStorage.setItem(key, JSON.stringify([...set])); }

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
}

type TimeFilter = 'todos' | 'hoy' | 'proximos' | 'finalizados';

export default function AppEventsTab() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<EventCategory | 'todos'>('todos');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('todos');
  const [selectedEvent, setSelectedEvent] = useState<RadioEvent | null>(null);
  const [interestSet, setInterestSet] = useState<Set<number>>(() => loadSet(INTEREST_KEY));
  const [attendSet, setAttendSet] = useState<Set<number>>(() => loadSet(ATTEND_KEY));
  const { hasReminder, toggleReminder } = useReminders();

  const allCategories = useMemo(() => {
    const cats = new Set(adminEvents.map(e => e.category));
    return Array.from(cats) as EventCategory[];
  }, []);

  const filtered = useMemo(() => {
    let result = [...adminEvents];
    if (timeFilter === 'hoy') result = result.filter(e => e.status === 'publicado');
    else if (timeFilter === 'proximos') result = result.filter(e => e.status === 'publicado');
    else if (timeFilter === 'finalizados') result = result.filter(e => e.status === 'finalizado');
    else result = result.filter(e => e.status !== 'cancelado');

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e => {
        const station = radioStations.find(s => s.id === e.stationId);
        return e.title.toLowerCase().includes(q) || e.description.toLowerCase().includes(q) || e.location.toLowerCase().includes(q) || (station?.name || '').toLowerCase().includes(q);
      });
    }
    if (category !== 'todos') result = result.filter(e => e.category === category);

    result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return result;
  }, [search, category, timeFilter]);

  const toggleInterest = useCallback((id: number) => {
    setInterestSet(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); saveSet(INTEREST_KEY, next); return next; });
  }, []);
  const toggleAttend = useCallback((id: number) => {
    setAttendSet(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); saveSet(ATTEND_KEY, next); return next; });
  }, []);

  const handleShare = useCallback((event: RadioEvent) => {
    const url = `${window.location.origin}/events?event=${event.id}`;
    if (navigator.share) {
      navigator.share({ title: event.title, text: event.description.slice(0, 100), url });
    } else {
      navigator.clipboard.writeText(url).then(() => SwalAlert.toast('Link copiado', 'success'));
    }
  }, []);

  const handleDirections = useCallback((event: RadioEvent) => {
    const q = encodeURIComponent(event.location);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, '_blank');
  }, []);

  const openDetail = useCallback((event: RadioEvent) => setSelectedEvent(event), []);

  const timeFilters: { id: TimeFilter; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todos', icon: 'ri-grid-line' },
    { id: 'hoy', label: 'Hoy', icon: 'ri-calendar-check-line' },
    { id: 'proximos', label: 'Próximos', icon: 'ri-calendar-2-line' },
    { id: 'finalizados', label: 'Finalizados', icon: 'ri-history-line' },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background-50 scrollbar-none">
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
            <i className="ri-calendar-event-line text-amber-600 text-lg"></i>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-foreground-950">Eventos</h2>
            <p className="text-[11px] text-foreground-400">{adminEvents.filter(e => e.status === 'publicado').length} activos · {adminEvents.length} totales</p>
          </div>
        </div>

        {/* Time filters */}
        <div className="flex gap-1.5 mb-3">
          {timeFilters.map(tf => (
            <button key={tf.id} onClick={() => setTimeFilter(tf.id)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                timeFilter === tf.id ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}>
              <i className={`${tf.icon} text-[11px]`}></i>{tf.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-2">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar eventos..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60" />
        </div>

        {/* Category chips */}
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setCategory('todos')}
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
              category === 'todos' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500'
            }`}>Todas</button>
          {allCategories.map(c => (
            <button key={c} onClick={() => setCategory(c === category ? 'todos' : c)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                category === c ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}>
              <i className={`${eventCategoryIcons[c]} text-[11px]`}></i>{eventCategoryLabels[c]}
            </button>
          ))}
        </div>
      </div>

      {/* Events count */}
      <div className="px-4 py-2">
        <p className="text-[11px] text-foreground-400">{filtered.length} evento{filtered.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Events list */}
      <div className="px-3 pb-6 space-y-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-calendar-2-line text-xl text-foreground-300"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-400">Sin eventos</p>
            <p className="text-[11px] text-foreground-300">Intentá ajustar los filtros</p>
          </div>
        ) : filtered.map(event => {
          const station = radioStations.find(s => s.id === event.stationId);
          const isInterest = interestSet.has(event.id);
          const isAttend = attendSet.has(event.id);
          const reminded = hasReminder(event.id, 'event');
          const typeColors = eventTypeColors[event.type];
          const isPast = event.status === 'finalizado';
          const isCancelled = event.status === 'cancelado';
          const dateObj = new Date(event.date);
          const day = dateObj.getDate();
          const month = dateObj.toLocaleDateString('es-MX', { month: 'short' });

          return (
            <div key={event.id} onClick={() => openDetail(event)}
              className={`bg-background-50 border rounded-2xl overflow-hidden transition-all cursor-pointer ${
                isPast ? 'border-background-200/70 opacity-80' : isCancelled ? 'border-red-200/70 opacity-70' : 'border-background-200 hover:border-foreground-300'
              }`}>
              <div className="flex">
                <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 overflow-hidden">
                  <img src={event.image} alt={event.title} className={`w-full h-full object-cover ${isPast ? 'grayscale-[30%]' : ''}`} />
                </div>
                <div className="flex-1 min-w-0 p-3 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
                        <i className={`${eventTypeIcons[event.type]} text-[8px]`}></i>{eventTypeLabels[event.type]}
                      </span>
                      <span className="text-[9px] text-foreground-400">{eventModalityLabels[event.modality]}</span>
                    </div>
                    <h3 className="text-xs font-bold text-foreground-950 leading-snug line-clamp-2">{event.title}</h3>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-foreground-500 mt-1">
                    <span className="flex items-center gap-1"><i className="ri-calendar-line text-[10px]"></i>{day} {month}</span>
                    <span className="flex items-center gap-1"><i className="ri-time-line text-[10px]"></i>{event.time}</span>
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line text-[10px]"></i>{event.location.split(',')[0]}</span>
                  </div>
                </div>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-1 px-3 pb-3 pt-0" onClick={e => e.stopPropagation()}>
                <button onClick={() => { toggleAttend(event.id); SwalAlert.toast(isAttend ? 'Asistencia cancelada' : '¡Asistirás al evento!', isAttend ? 'info' : 'success'); }}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    isAttend ? 'bg-emerald-100 text-emerald-700' : 'bg-background-100 text-foreground-500 hover:bg-emerald-50'
                  }`}>
                  <i className={`text-[10px] ${isAttend ? 'ri-calendar-check-fill' : 'ri-calendar-check-line'}`}></i>
                  {isAttend ? 'Voy' : 'Asistir'}
                </button>
                <button onClick={() => toggleInterest(event.id)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    isInterest ? 'bg-red-100 text-red-600' : 'bg-background-100 text-foreground-500 hover:bg-red-50'
                  }`}>
                  <i className={`text-[10px] ${isInterest ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                  {event.interested + (isInterest ? 1 : 0)}
                </button>
                <button onClick={() => handleShare(event)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-background-100 text-foreground-500 hover:bg-violet-50 whitespace-nowrap cursor-pointer">
                  <i className="ri-share-forward-line text-[10px]"></i>
                </button>
                <div className="flex-1"></div>
                <button onClick={() => {
                  const added = toggleReminder(event.id, 'event', event.title, event.date);
                  SwalAlert.toast(added ? 'Recordatorio activado' : 'Recordatorio cancelado', added ? 'success' : 'info');
                }}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] transition-all cursor-pointer ${
                    reminded ? 'bg-amber-100 text-amber-600' : 'bg-background-100 text-foreground-400'
                  }`}>
                  <i className={`${reminded ? 'ri-notification-3-fill' : 'ri-notification-3-line'} text-[12px]`}></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── DETAIL MODAL ─── */}
      {selectedEvent && (() => {
        const event = selectedEvent;
        const station = radioStations.find(s => s.id === event.stationId);
        const isInterest = interestSet.has(event.id);
        const isAttend = attendSet.has(event.id);
        const reminded = hasReminder(event.id, 'event');
        const isPast = event.status === 'finalizado';
        const isCancelled = event.status === 'cancelado';
        const isValidEvent = !isPast && !isCancelled;
        const typeColors = eventTypeColors[event.type];
        const dateObj = new Date(event.date);
        const day = dateObj.getDate();
        const month = dateObj.toLocaleDateString('es-MX', { month: 'long' });
        const year = dateObj.getFullYear();
        const weekday = dateObj.toLocaleDateString('es-MX', { weekday: 'long' });

        return (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedEvent(null)}></div>
            <div className="relative bg-background-50 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl animate-[slideUp_0.2s_ease-out]">
              <button onClick={() => setSelectedEvent(null)} className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-md flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-700"></i>
              </button>
              <div className="relative h-44 overflow-hidden rounded-t-3xl">
                <img src={event.image} alt={event.title} className={`w-full h-full object-cover object-top ${isPast ? 'grayscale-[30%]' : ''}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute top-3 left-3 flex items-center gap-1.5">
                  <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center shrink-0 ${event.status === 'publicado' ? 'bg-red-500 text-white' : isPast ? 'bg-foreground-400 text-white' : 'bg-foreground-400 text-white'}`}>
                    <span className="text-[9px] font-bold uppercase leading-none">{month.slice(0,3)}</span>
                    <span className="text-base font-extrabold leading-none">{day}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${event.status === 'publicado' ? 'bg-accent-500 text-white' : isCancelled ? 'bg-red-500 text-white' : 'bg-foreground-300 text-foreground-700'}`}>
                    {eventStatusLabels[event.status]}
                  </span>
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
                      <i className={`${eventTypeIcons[event.type]} text-[8px]`}></i>{eventTypeLabels[event.type]}
                    </span>
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${event.modality === 'presencial' ? 'bg-amber-50 text-amber-700' : event.modality === 'online' ? 'bg-sky-50 text-sky-700' : 'bg-violet-50 text-violet-700'}`}>
                      <i className={`${eventModalityIcons[event.modality]} text-[8px]`}></i>{eventModalityLabels[event.modality]}
                    </span>
                  </div>
                  <h3 className="text-lg font-extrabold text-white leading-tight">{event.title}</h3>
                </div>
              </div>
              <div className="p-4 space-y-4">
                <p className="text-sm text-foreground-700 leading-relaxed">{event.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2.5 rounded-xl bg-background-100">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Fecha</p>
                    <p className="text-xs font-bold text-foreground-900 capitalize">{weekday}, {day} de {month} de {year}</p>
                  </div>
                  <div className="p-2.5 rounded-xl bg-background-100">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Horario</p>
                    <p className="text-xs font-bold text-foreground-900">{event.time}</p>
                  </div>
                  <div className="p-2.5 rounded-xl bg-background-100">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Ubicación</p>
                    <p className="text-[11px] font-medium text-foreground-700 truncate">{event.location}</p>
                  </div>
                  <div className="p-2.5 rounded-xl bg-background-100">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Categoría</p>
                    <p className="text-xs font-bold text-foreground-900 flex items-center gap-1">
                      <i className={`${eventCategoryIcons[event.category]} text-[11px] text-accent-600`}></i>{eventCategoryLabels[event.category]}
                    </p>
                  </div>
                </div>
                {station && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-background-100">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-200 shrink-0">
                      <img src={station.logoUrl} alt={station.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-foreground-400">Organiza</p>
                      <p className="text-xs font-bold text-foreground-900">{station.name}</p>
                    </div>
                    <span className="text-[10px] text-foreground-400 bg-background-200 rounded-lg px-2 py-0.5 ml-auto">{station.frequency} RFW</span>
                  </div>
                )}
                <div className="grid grid-cols-4 gap-2">
                  {[{ label: 'Participantes', value: event.participants.toLocaleString(), icon: 'ri-group-line' },{ label: 'Interesados', value: (event.interested + (isInterest ? 1 : 0)).toLocaleString(), icon: 'ri-heart-line' },{ label: 'Compartidos', value: event.shares.toLocaleString(), icon: 'ri-share-forward-line' },{ label: 'Seguidores', value: event.followers.toLocaleString(), icon: 'ri-user-follow-line' }].map(s => (
                    <div key={s.label} className="text-center p-2 rounded-lg bg-background-100">
                      <i className={`${s.icon} text-sm text-foreground-400 mb-0.5 block`}></i>
                      <p className="text-sm font-bold text-foreground-900">{s.value}</p>
                      <p className="text-[8px] text-foreground-400">{s.label}</p>
                    </div>
                  ))}
                </div>
                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-background-100">
                  {isValidEvent && (
                    <>
                      <button onClick={() => { toggleAttend(event.id); SwalAlert.toast(isAttend ? 'Asistencia cancelada' : '¡Asistirás!', isAttend ? 'info' : 'success'); }}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                          isAttend ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        }`}>
                        <i className={`text-[11px] ${isAttend ? 'ri-calendar-check-fill' : 'ri-calendar-check-line'}`}></i>
                        {isAttend ? 'Voy a Asistir' : 'Asistiré'}
                      </button>
                      <button onClick={() => { toggleInterest(event.id); SwalAlert.toast(isInterest ? 'Interés quitado' : '¡Te interesa!', isInterest ? 'info' : 'success'); }}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                          isInterest ? 'bg-red-500 text-white' : 'bg-red-50 text-red-600 hover:bg-red-100'
                        }`}>
                        <i className={`text-[11px] ${isInterest ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>Me Interesa
                      </button>
                    </>
                  )}
                  <button onClick={() => handleShare(event)} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-violet-50 text-violet-700 hover:bg-violet-100 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line text-[11px]"></i>Compartir
                  </button>
                  {event.modality !== 'online' && isValidEvent && (
                    <button onClick={() => handleDirections(event)} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-sky-50 text-sky-700 hover:bg-sky-100 transition-all whitespace-nowrap cursor-pointer">
                      <i className="ri-navigation-line text-[11px]"></i>Cómo llegar
                    </button>
                  )}
                  {isValidEvent && (
                    <button onClick={() => {
                      const added = toggleReminder(event.id, 'event', event.title, event.date);
                      SwalAlert.toast(added ? 'Recordatorio activado' : 'Recordatorio cancelado', added ? 'success' : 'info');
                    }}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                        reminded ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                      }`}>
                      <i className={`text-[11px] ${reminded ? 'ri-notification-3-fill' : 'ri-notification-3-line'}`}></i>
                      {reminded ? 'Recordado' : 'Recordarme'}
                    </button>
                  )}
                  {isCancelled && <span className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-red-50 text-red-700"><i className="ri-close-circle-line"></i>Cancelado</span>}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}